<template>
  <v-dialog
    :value="value"
    persistent
    max-width="820px"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-toolbar
        dense
        dark
        color="deep-orange"
        elevation="2"
      >
        <v-toolbar-title>
          {{ tituloDialogo }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn
          icon
          :disabled="loading"
          @click="cerrar"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-5">
        <v-form
          ref="form"
          v-model="formularioValido"
          lazy-validation
        >
          <v-subheader class="px-0">
            Información general
          </v-subheader>

          <v-row>
            <v-col cols="12" md="7">
              <v-text-field
                v-model.trim="form.nombre"
                label="Nombre"
                maxlength="255"
                counter
                :disabled="loading"
                :rules="reglas.requerido"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="5">
              <v-select
                v-model="form.grupo_codigo"
                :items="grupos"
                item-value="codigo"
                item-text="nombre"
                label="Grupo"
                :loading="loadingCatalogos"
                :disabled="
                  loading ||
                  loadingCatalogos
                "
                :rules="reglas.requerido"
              ></v-select>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model.trim="form.descripcion"
                label="Descripción"
                rows="2"
                maxlength="1000"
                counter
                :disabled="loading"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <v-subheader class="px-0">
            Archivo
          </v-subheader>

          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="form.formato"
                :items="formatos"
                item-value="valor"
                item-text="texto"
                label="Formato"
                :disabled="loading"
                :rules="reglas.requerido"
              ></v-select>
            </v-col>

            <v-col cols="12" md="8">
              <v-text-field
                v-model.trim="form.prefijo_archivo"
                label="Prefijo del archivo"
                hint="Ejemplo: REPORTE_JTI"
                persistent-hint
                maxlength="255"
                :disabled="loading"
                :rules="reglas.requerido"
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <div class="subtitle-2 mb-2">
                Nombre del archivo
              </div>

              <v-radio-group
                v-model="
                  form.usar_nombre_fijo
                "
                row
                :disabled="loading"
                class="mt-0"
              >
                <v-radio
                  label="
                    Agregar fecha y hora
                    al nombre
                  "
                  :value="false"
                ></v-radio>

                <v-radio
                  label="
                    Mantener siempre
                    el mismo nombre
                  "
                  :value="true"
                ></v-radio>
              </v-radio-group>

              <v-alert
                dense
                outlined
                :type="
                  form.usar_nombre_fijo
                    ? 'warning'
                    : 'info'
                "
              >
                <div>
                  Nombre resultante:
                  <strong>
                    {{
                      nombreArchivoEjemplo
                    }}
                  </strong>
                </div>

                <div
                  v-if="
                    form.usar_nombre_fijo
                  "
                  class="caption mt-1"
                >
                  Cada ejecución reemplazará
                  el archivo anterior en
                  OneDrive.
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12">
              <v-switch
                v-model="form.aplicar_deduplicacion"
                label="Aplicar deduplicación"
                hint="Conserva un solo registro cuando el reporte contiene filas duplicadas."
                persistent-hint
                :disabled="loading"
                color="primary"
                class="mt-0"
              ></v-switch>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <v-subheader class="px-0">
            Periodo y programación
          </v-subheader>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.periodo_tipo"
                :items="periodos"
                item-value="valor"
                item-text="texto"
                label="Datos que se exportarán"
                :disabled="loading"
                :rules="reglas.requerido"
              ></v-select>
            </v-col>

            <v-col
              v-if="
                form.periodo_tipo ===
                'ultimos_n_dias'
              "
              cols="12"
              md="3"
            >
              <v-text-field
                v-model.number="
                  form.cantidad_dias
                "
                type="number"
                min="1"
                max="365"
                label="Cantidad de días"
                :disabled="loading"
                :rules="reglas.cantidadDias"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <v-menu
                ref="menuHora"
                v-model="menuHora"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template
                  v-slot:activator="{ on, attrs }"
                >
                  <v-text-field
                    v-model="form.hora"
                    label="Hora diaria"
                    prepend-icon="mdi-clock-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :disabled="loading"
                    :rules="reglas.requerido"
                  ></v-text-field>
                </template>

                <v-time-picker
                  v-if="menuHora"
                  v-model="form.hora"
                  format="24hr"
                  full-width
                  @click:minute="
                    menuHora = false
                  "
                ></v-time-picker>
              </v-menu>
            </v-col>

            <v-col cols="12">
              <v-alert
                dense
                outlined
                type="info"
              >
                Se ejecutará diariamente a las
                <strong>{{ form.hora }}</strong>
                en la zona
                <strong>
                  America/Monterrey
                </strong>.
              </v-alert>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <v-subheader class="px-0">
            Destino OneDrive
          </v-subheader>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="
                  form
                    .conexion_onedrive_codigo
                "
                :items="conexiones"
                item-value="codigo"
                :item-text="textoConexion"
                label="Conexión de OneDrive"
                :loading="loadingCatalogos"
                :disabled="
                  loading ||
                  loadingCatalogos
                "
                :rules="reglas.requerido"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="
                  form.carpeta_onedrive
                "
                label="Carpeta de OneDrive"
                hint="Ejemplo: Censo MKD/Reportes/JTI"
                persistent-hint
                maxlength="1000"
                :disabled="loading"
                :rules="reglas.requerido"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>

        <v-alert
          v-if="error"
          type="error"
          dense
          outlined
          class="mt-4"
        >
          {{ mensajeError }}
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn
          text
          :disabled="loading"
          @click="cerrar"
        >
          Cancelar
        </v-btn>

        <v-btn
          color="deep-orange"
          dark
          :loading="loading"
          @click="guardar"
        >
          {{ textoBotonGuardar }}
          <v-icon right>
            mdi-content-save
          </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  actualizarExportacionProgramada,
  crearExportacionProgramada,
  listarCatalogosExportaciones,
} from "@/utils/exportacionesProgramadasApi";

function formularioInicial() {
  return {
    nombre: "",
    descripcion: "",
    grupo_codigo: "",
    formato: "csv",
    prefijo_archivo: "",
    usar_nombre_fijo: false,
    aplicar_deduplicacion: true,
    periodo_tipo: "dia_anterior",
    cantidad_dias: 7,
    hora: "06:00",
    conexion_onedrive_codigo: "",
    carpeta_onedrive:
      "Censo MKD/Reportes",
  };
}

export default {
  name: "ExportacionProgramadaDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },

    configuracion: {
      type: Object,
      default: null,
    },

    modo: {
      type: String,
      default: "crear",
      validator: valor =>
        [
          "crear",
          "editar",
          "duplicar",
        ].includes(valor),
    },
  },

  data: () => ({
    loading: false,
    loadingCatalogos: false,
    formularioValido: false,
    menuHora: false,
    error: false,
    mensajeError: "",
    grupos: [],
    conexiones: [],
    form: formularioInicial(),

    formatos: [
      {
        valor: "csv",
        texto: "CSV",
      },
      {
        valor: "txt",
        texto: "TXT",
      },
    ],

    periodos: [
      {
        valor: "dia_anterior",
        texto: "Día anterior",
      },
      {
        valor: "dia_actual",
        texto: "Día actual",
      },
      {
        valor: "ultimos_n_dias",
        texto: "Últimos N días",
      },
      {
        valor: "mes_anterior",
        texto: "Mes anterior",
      },
    ],

    reglas: {
      requerido: [
        (valor) =>
          !!valor ||
          "Este campo es requerido",
      ],

      cantidadDias: [
        (valor) =>
          Number.isInteger(Number(valor)) ||
          "Debe ser un número entero",
        (valor) =>
          Number(valor) >= 1 ||
          "Debe ser mayor o igual a 1",
        (valor) =>
          Number(valor) <= 365 ||
          "No puede ser mayor a 365",
      ],
    },
  }),

  computed: {
    tieneConfiguracion() {
      return Boolean(
        this.configuracion &&
        this.configuracion.codigo
      );
    },

    esEdicion() {
      return (
        this.modo === "editar" &&
        this.tieneConfiguracion
      );
    },

    esDuplicacion() {
      return (
        this.modo === "duplicar" &&
        this.tieneConfiguracion
      );
    },

    tituloDialogo() {
      if (this.esEdicion) {
        return "Editar programación";
      }

      if (this.esDuplicacion) {
        return "Duplicar programación";
      }

      return "Nueva programación";
    },

    textoBotonGuardar() {
      if (this.esEdicion) {
        return "Actualizar programación";
      }

      if (this.esDuplicacion) {
        return "Crear copia";
      }

      return "Guardar programación";
    },

    nombreArchivoEjemplo() {
      const prefijo =
        this.form.prefijo_archivo ||
        "REPORTE";

      const extension =
        this.form.formato === "txt"
          ? "txt"
          : "csv";

      if (
        this.form.usar_nombre_fijo
      ) {
        return (
          `${prefijo}.${extension}`
        );
      }

      return (
        `${prefijo}_` +
        "2026-08-04_06-00-00." +
        extension
      );
    },
  },

  watch: {
    configuracion: {
      deep: true,
      handler() {
        if (
          this.value &&
          this.tieneConfiguracion &&
          !this.loadingCatalogos
        ) {
          this.cargarConfiguracion();
        }
      },
    },

    value(valor) {
      if (valor) {
        this.preparar();
      }
    },

    "form.nombre"(nombre) {
      if (this.esEdicion) {
        return;
      }

      if (
        !this.form.prefijo_archivo ||
        this.form.prefijo_archivo ===
          this.prefijoAnterior
      ) {
        const prefijo =
          this.generarPrefijo(nombre);

        this.prefijoAnterior =
          prefijo;

        this.form.prefijo_archivo =
          prefijo;
      }
    },
  },

  methods: {
    async preparar() {
      this.error = false;
      this.mensajeError = "";
      this.form = formularioInicial();
      this.prefijoAnterior = "";

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      await this.cargarCatalogos();

      if (
        this.esEdicion ||
        this.esDuplicacion
      ) {
        this.cargarConfiguracion();
      }
    },

    async cargarCatalogos() {
      this.loadingCatalogos = true;

      try {
        const resultado =
          await listarCatalogosExportaciones(
            this.$session
          );

        this.grupos =
          resultado.grupos;

        this.conexiones =
          resultado.conexiones;

        if (
          !this.esEdicion &&
          !this.esDuplicacion &&
          this.conexiones.length === 1
        ) {
          this.form
            .conexion_onedrive_codigo =
            this.conexiones[0].codigo;
        }
      } catch (error) {
        console.error(error);

        this.mostrarError(
          "No fue posible cargar los catálogos"
        );
      } finally {
        this.loadingCatalogos = false;
      }
    },

    cargarConfiguracion() {
      const configuracion =
        this.configuracion || {};

      const periodo =
        configuracion.periodo || {};

      const programacion =
        configuracion.programacion || {};

      const onedrive =
        configuracion.onedrive || {};

      this.form = {
        nombre:
          configuracion.nombre || "",
        descripcion:
          configuracion.descripcion || "",
        grupo_codigo:
          configuracion.grupo
            ? configuracion.grupo.codigo
            : "",
        formato:
          configuracion.formato ||
          "csv",
        prefijo_archivo:
          configuracion.prefijo_archivo ||
          "",
        usar_nombre_fijo:
          configuracion
            .usar_nombre_fijo === true,
        aplicar_deduplicacion:
          configuracion
            .aplicar_deduplicacion !==
          false,
        periodo_tipo:
          periodo.tipo ||
          "dia_anterior",
        cantidad_dias:
          periodo.cantidadDias ||
          periodo.cantidad_dias ||
          7,
        hora:
          this.obtenerHoraCron(
            programacion.expresion_cron
          ),
        conexion_onedrive_codigo:
          onedrive.codigo || "",
        carpeta_onedrive:
          onedrive.carpeta ||
          "Censo MKD/Reportes",
      };

      if (this.esDuplicacion) {
        const nombreBase =
          this.form.nombre
            .replace(
              /\s*\(copia(?:\s+\d+)?\)$/i,
              ""
            )
            .trim();

        this.form.nombre =
          `${nombreBase} (copia)`;

        const prefijoBase =
          String(
            this.form.prefijo_archivo ||
            "REPORTE"
          )
            .replace(
              /_COPIA(?:_\d+)?$/i,
              ""
            );

        this.form.prefijo_archivo =
          `${prefijoBase}_COPIA`
            .slice(0, 255);
      }

      this.prefijoAnterior =
        this.form.prefijo_archivo;
    },

    obtenerHoraCron(expresionCron) {
      const partes =
        String(expresionCron || "")
          .trim()
          .split(/\s+/);

      if (partes.length < 2) {
        return "06:00";
      }

      const minuto =
        Number(partes[0]);

      const hora =
        Number(partes[1]);

      if (
        !Number.isInteger(hora) ||
        !Number.isInteger(minuto) ||
        hora < 0 ||
        hora > 23 ||
        minuto < 0 ||
        minuto > 59
      ) {
        return "06:00";
      }

      return (
        String(hora).padStart(
          2,
          "0"
        ) +
        ":" +
        String(minuto).padStart(
          2,
          "0"
        )
      );
    },

    textoConexion(item) {
      if (!item) {
        return "";
      }

      return item.cuenta
        ? `${item.nombre} — ${item.cuenta}`
        : item.nombre;
    },

    generarPrefijo(nombre) {
      return String(nombre || "")
        .normalize("NFD")
        .replace(
          /[\u0300-\u036f]/g,
          ""
        )
        .replace(/[^A-Za-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase()
        .slice(0, 255);
    },

    generarCron() {
      const partes =
        String(this.form.hora).split(":");

      const hora = Number(partes[0]);
      const minuto = Number(partes[1]);

      if (
        !Number.isInteger(hora) ||
        !Number.isInteger(minuto) ||
        hora < 0 ||
        hora > 23 ||
        minuto < 0 ||
        minuto > 59
      ) {
        throw new Error(
          "La hora seleccionada no es válida"
        );
      }

      return `${minuto} ${hora} * * *`;
    },

    construirPeriodo() {
      const periodo = {
        tipo:
          this.form.periodo_tipo,
      };

      if (
        periodo.tipo ===
        "ultimos_n_dias"
      ) {
        periodo.cantidadDias =
          Number(
            this.form.cantidad_dias
          );
      }

      return periodo;
    },

    async guardar() {
      this.error = false;

      const valido =
        this.$refs.form.validate();

      if (!valido) {
        this.mostrarError(
          "Revisa los campos obligatorios"
        );
        return;
      }

      this.loading = true;

      try {
        const datos = {
              nombre: this.form.nombre,
              descripcion:
                this.form.descripcion ||
                null,
              grupo_codigo:
                this.form.grupo_codigo,
              formato:
                this.form.formato,
              prefijo_archivo:
                this.form
                  .prefijo_archivo,
              usar_nombre_fijo:
                this.form
                  .usar_nombre_fijo,
              aplicar_deduplicacion:
                this.form
                  .aplicar_deduplicacion,
              zona_horaria:
                "America/Monterrey",
              expresion_cron:
                this.generarCron(),
              periodo:
                this.construirPeriodo(),
              conexion_onedrive_codigo:
                this.form
                  .conexion_onedrive_codigo,
          carpeta_onedrive:
            this.form
              .carpeta_onedrive,
        };

        const resultado =
          this.esEdicion
            ? await actualizarExportacionProgramada(
                this.$session,
                this.configuracion.codigo,
                datos
              )
            : await crearExportacionProgramada(
                this.$session,
                datos
              );

        if (!resultado.correcto) {
          this.mostrarError(
            resultado.mensaje ||
              "No fue posible guardar la programación"
          );
          return;
        }

        this.$emit(
          "guardada",
          resultado
        );

        this.cerrar();
      } catch (error) {
        console.error(error);

        const mensaje =
          error.response &&
          error.response.data &&
          error.response.data.mensaje
            ? error.response.data.mensaje
            : error.message ||
              "No fue posible guardar la programación";

        this.mostrarError(mensaje);
      } finally {
        this.loading = false;
      }
    },

    cerrar() {
      if (this.loading) {
        return;
      }

      this.$emit("input", false);
    },

    mostrarError(mensaje) {
      this.error = true;
      this.mensajeError = mensaje;
    },
  },
};
</script>
